import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import useDuplicatedCallback from "../helpers/useDeduplicatedCallback";

// Create Scanning Context
const ScanningContext = createContext({
    scanningCapabilities: 'none',
    displayScanButton: false,
    scanInProgress: false,
    startScan: () => { },
    stopScan: () => { },
    triggerScan: () => { },
    registerCallback: () => { },
    unregisterCallback: () => { },
})

// Provider Component
export const ScanningProvider = ({ children }) => {
    const [activeCallbacks, setActiveCallbacks] = useState(new Set())
    const [scanInProgress, setScanInProgress] = useState(false);
    const scanningCapabilities = useMemo(() => {
        return window?.Android?.getScanningCapabilities?.()
    }, []);

    const hasActiveCallbacks = activeCallbacks.size > 0
    //TMP Currently disabling display of scan button as camera scanning is not stable enough
    const displayScanButton = useMemo(() => false && scanningCapabilities === 'camera' && hasActiveCallbacks, [scanningCapabilities, hasActiveCallbacks]);


    const barcodeCameraEventHandler = useDuplicatedCallback((event) => {
        if (event.value === 'start') {
            setScanInProgress(true);
        }
        else if (event.value === 'stop') {
            setScanInProgress(false);
        }
    },[], 300);

    useEffect(() => {
        window.addEventListener('barcode.camera', barcodeCameraEventHandler, false)

        return () => {
            window.removeEventListener('barcode.camera', barcodeCameraEventHandler);
        }
    }
        , [barcodeCameraEventHandler])

    const startScan = useCallback(() => {
        if (scanningCapabilities === 'camera' && window?.Android?.enableCameraScanning) {
            window.Android.enableCameraScanning();
            setScanInProgress(true);
        }
    }, [scanningCapabilities]);

    const stopScan = useCallback(() => {
        if (scanningCapabilities === 'camera' && window?.Android?.disableCameraScanning) {
            window.Android.disableCameraScanning();
            setScanInProgress(false);
        }
    }, [])

    const registerCallback = useCallback((id) => {
        setActiveCallbacks(prev => new Set(prev).add(id))
    }, [])

    const unregisterCallback = useCallback((id) => {
        setActiveCallbacks(prev => {
            const next = new Set(prev)
            next.delete(id)

            // If no more callbacks and scan is in progress, stop it
            if (next.size === 0 && scanInProgress) {
                if (scanningCapabilities === 'camera' && window?.Android?.disableCameraScanning) {
                    window.Android.disableCameraScanning();
                    setScanInProgress(false);
                }
            }

            return next
        })
    }, [scanInProgress, scanningCapabilities])

    const contextValue = useMemo(() => ({
        scanningCapabilities,
        displayScanButton,
        scanInProgress,
        startScan,
        stopScan,
        registerCallback,
        unregisterCallback,
    }), [scanningCapabilities, displayScanButton, scanInProgress, startScan, stopScan, registerCallback, unregisterCallback])

    return (
        <ScanningContext.Provider value={contextValue}>
            {children}
        </ScanningContext.Provider>
    )
}

export const useScanningContext = () => useContext(ScanningContext)
